import React, { useState, useEffect } from 'react';
import { Table, Button, Space, Modal, Form, Input, message, Select, Card, Row, Col, Statistic, List } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, DownloadOutlined, SearchOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { volunteerService, Volunteer } from '../services/volunteerService';
import { getProjects, Project } from '../services/project';
import { authService } from '../services/authService';
import { doctorService, Doctor } from '../services/doctorService';
import { useNavigate } from 'react-router-dom';
import useResponsive from '../hooks/useResponsive';

const { Option } = Select;

const VolunteerManagement: React.FC = () => {
  const navigate = useNavigate();
  const { isMobile } = useResponsive();
  const [volunteers, setVolunteers] = useState<Volunteer[]>([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingVolunteer, setEditingVolunteer] = useState<Volunteer | null>(null);
  const [form] = Form.useForm();
  const [searchForm] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [projects, setProjects] = useState<Project[]>([]);
  const [doctors, setDoctors] = useState<Doctor[]>([]);
  const [doctorSearchLoading, setDoctorSearchLoading] = useState(false);
  const [doctorSearchValue, setDoctorSearchValue] = useState('');
  const [levelConfigs, setLevelConfigs] = useState<string[]>([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [searchParams, setSearchParams] = useState({
    project_id: undefined,
    doctor_id: undefined,
    keyword: '',
    levels: {} as Record<string, string>,
  });

  // 获取当前用户信息
  const currentUser = authService.getCurrentUser();

  // 获取项目列表
  const fetchProjects = async () => {
    try {
      const response = await getProjects();
      if (response.success) {
        setProjects(response.data);
      }
    } catch (error) {
      message.error('获取项目列表失败');
    }
  };

  // 搜索医生列表
  const searchDoctors = async (searchValue: string) => {
    setDoctorSearchValue(searchValue);

    if (!searchValue || searchValue.length < 2) {
      setDoctors([]);
      return;
    }

    try {
      setDoctorSearchLoading(true);
      const response = await doctorService.getDoctors({
        name: searchValue, // 按医生姓名搜索
        page: 1,
        pageSize: 50 // 限制搜索结果数量
      });
      if (response.success) {
        setDoctors(response.data);
      }
    } catch (error) {
      message.error('搜索医生失败');
    } finally {
      setDoctorSearchLoading(false);
    }
  };


  useEffect(() => {
    fetchProjects();
  }, []);

  useEffect(() => {
    fetchVolunteers();
  }, [pagination.current, pagination.pageSize, searchParams]);

  const fetchVolunteers = async () => {
    try {
      setLoading(true);
      const response = await volunteerService.getVolunteers({
        page: pagination.current,
        pageSize: pagination.pageSize,
        ...searchParams
      });
      if (response.code === 0) {
        setVolunteers(response.data.list);
        setPagination(prev => ({
          ...prev,
          total: response.data.total
        }));
      }
    } catch (error) {
      message.error('获取志愿者列表失败');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (values: any) => {
    setSearchParams(values);
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const handleReset = () => {
    searchForm.resetFields();
    setSearchParams({
      project_id: undefined,
      doctor_id: undefined,
      keyword: '',
      levels: {},
    });
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const columns: ColumnsType<Volunteer> = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
    },
    {
      title: '姓名',
      dataIndex: 'name',
      key: 'name',
      width: 120,
    },
    {
      title: '联系电话',
      dataIndex: 'phone',
      key: 'phone',
      width: 120,
    },
    {
      title: "负责人",
      dataIndex: "responsible_person",
      key: "responsible_person",
      render: (_, record: Volunteer) => {
        const volunteerRecord = record as any;
        // 如果有关联记录，优先显示关联表中的负责人信息
        if (volunteerRecord.doctorVolunteers && volunteerRecord.doctorVolunteers.length > 0) {
          // 提取所有负责人信息并去重
          const responsibles: any[] = [];
          const seen = new Set();
          
          volunteerRecord.doctorVolunteers.forEach((dv: any) => {
            if (dv.responsible_person_name) {
              const key = `${dv.responsible_person_name}-${dv.responsible_person_phone}`;
              if (!seen.has(key)) {
                seen.add(key);
                responsibles.push({
                  name: dv.responsible_person_name,
                  phone: dv.responsible_person_phone,
                  project: dv.project?.name
                });
              }
            }
          });

          if (responsibles.length > 0) {
            return (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                {responsibles.map((resp, idx) => (
                  <div key={idx} style={{ fontSize: '13px' }}>
                    {resp.name} ({resp.phone})
                    {responsibles.length > 1 && resp.project && (
                      <span style={{ color: '#8c8c8c', marginLeft: 4, fontSize: '12px' }}>
                        [{resp.project}]
                      </span>
                    )}
                  </div>
                ))}
              </div>
            );
          }
        }
        // 回退显示根属性（兼容旧数据）
        return <div>{record.responsible_person_name} ({record.responsible_person_phone})</div>
      },
      width: 250,
    },
    {
      title: '所属项目',
      dataIndex: 'project',
      key: 'project',
      width: 180,
      render: (_, record: Volunteer) => {
        const volunteerRecord = record as any;
        if (volunteerRecord.doctorVolunteers && volunteerRecord.doctorVolunteers.length > 0) {
          // 提取所有项目并去重
          const projects = new Map();
          volunteerRecord.doctorVolunteers.forEach((dv: any) => {
            if (dv.project) {
              projects.set(dv.project.id, dv.project.name);
            }
          });
          
          if (projects.size > 0) {
            return (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                {Array.from(projects.values()).map((name: any, idx) => (
                  <div key={idx}>{name}</div>
                ))}
              </div>
            );
          }
        }
        return '未分配项目';
      },
    },
    {
      title: '关联医生',
      dataIndex: 'doctors',
      key: 'doctors',
      width: 200,
      render: (doctors: any[], record: Volunteer) => {
        const volunteerRecord = record as any;
        const currentProjectId = searchParams.project_id;
        
        // 如果有 doctorVolunteers 关联数据，利用它来进行精确匹配和展示
        if (volunteerRecord.doctorVolunteers && volunteerRecord.doctorVolunteers.length > 0) {
          const displayItems: any[] = [];
          const doctorMap = new Map((doctors || []).map((d: any) => [d.id, d]));
          const processedKeys = new Set(); // 防止重复

          volunteerRecord.doctorVolunteers.forEach((dv: any) => {
            // 如果有项目筛选，且不匹配，则跳过
            if (currentProjectId && dv.project_id !== currentProjectId) return;
            
            // 尝试获取对应的医生信息
            const doctor = doctorMap.get(dv.doctor_id);
            if (doctor) {
              const key = `${dv.doctor_id}-${dv.project_id}`;
              if (!processedKeys.has(key)) {
                processedKeys.add(key);
                displayItems.push({
                  doctor: doctor,
                  project: dv.project
                });
              }
            }
          });

          if (displayItems.length > 0) {
            return (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                {displayItems.map((item, idx) => (
                  <div key={idx} style={{ fontSize: '13px' }}>
                    {item.doctor.name} ({item.doctor.phone})
                    {/* 显示项目名称，增加清晰度 */}
                    {item.project && (
                      <span style={{ color: '#8c8c8c', marginLeft: 4, fontSize: '12px' }}>
                        [{item.project.name}]
                      </span>
                    )}
                  </div>
                ))}
              </div>
            );
          }
          
          // 如果有筛选项目，但没有找到匹配的医生，显示 '-'
          if (currentProjectId) return '-';
        }

        // 回退逻辑：如果没有 doctorVolunteers 数据，或者处于非筛选状态且上述逻辑未执行
        if (!doctors || doctors.length === 0) return '-';
        
        // 如果有筛选项目，直接隐藏不确定的医生信息（避免误导）
        if (currentProjectId) return '-';

        return doctors.map(doctor => (
          <div key={doctor.id}>
            {doctor.name} ({doctor.phone})
          </div>
        ));
      },
    },
    {
      title: '层级信息',
      dataIndex: 'levels',
      key: 'levels',
      width: 200,
      render: (levels: any, record: Volunteer) => {
        const volunteerRecord = record as any;
        
        // 尝试从关联记录中获取所有层级信息
        if (volunteerRecord.doctorVolunteers && volunteerRecord.doctorVolunteers.length > 0) {
            const allLevels: any[] = [];
            const seenProjects = new Set();
            
            volunteerRecord.doctorVolunteers.forEach((dv: any) => {
                // 确保有项目信息且去重
                if (dv.project && !seenProjects.has(dv.project.id)) {
                    if (dv.levels && Array.isArray(dv.levels) && dv.levels.length > 0) {
                        seenProjects.add(dv.project.id);
                        allLevels.push({
                            project: dv.project.name,
                            levels: dv.levels
                        });
                    }
                }
            });
            
            if (allLevels.length > 0) {
                 return (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        {allLevels.map((item, idx) => (
                            <div key={idx} style={{ borderBottom: idx < allLevels.length - 1 ? '1px dashed #f0f0f0' : 'none', paddingBottom: idx < allLevels.length - 1 ? '4px' : '0' }}>
                                {allLevels.length > 1 && item.project && (
                                    <div style={{ color: '#8c8c8c', fontSize: '12px', marginBottom: '2px' }}>[{item.project}]</div>
                                )}
                                {(item.levels as Array<{name: string, value: string, order: number}>)
                                  .sort((a, b) => (a.order || 0) - (b.order || 0))
                                  .map((level, lIdx) => (
                                    <div key={lIdx} style={{ fontSize: '13px' }}>{level.name}: {level.value}</div>
                                  ))
                                }
                            </div>
                        ))}
                    </div>
                 );
            }
        }

        // 回退逻辑
        if (!levels || !Array.isArray(levels) || levels.length === 0) return '-';
        
        return (levels as Array<{name: string, value: string, order: number}>)
          .sort((a, b) => (a.order || 0) - (b.order || 0))
          .map((item, index) => (
            <div key={index}>{item.name}: {item.value}</div>
          ));
      },
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status: string) => status === 'active' ? '活跃' : '非活跃',
    },
    {
      title: '操作',
      key: 'action',
      width: 250,
      fixed: 'right',
      render: (_, record) => (
        <Space size="middle">
          <Button
            type="primary"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            编辑
          </Button>
          <Button
            type="primary"
            danger
            icon={<DeleteOutlined />}
            onClick={() => handleDelete(record.id)}
          >
            删除
          </Button>
        </Space>
      ),
    },
  ];

  // 移动端卡片渲染
  const renderMobileCard = (volunteer: Volunteer) => (
    <Card 
      key={volunteer.id}
      style={{ marginBottom: 16 }}
      size="small"
    >
      <div style={{ marginBottom: 8 }}>
        <strong>姓名：</strong>{volunteer.name}
      </div>
      <div style={{ marginBottom: 8 }}>
        <strong>电话：</strong>{volunteer.phone}
      </div>
      <div style={{ marginBottom: 8 }}>
        <strong>负责人：</strong>{volunteer.responsible_person_name} ({volunteer.responsible_person_phone})
      </div>
      {volunteer.levels && Array.isArray(volunteer.levels) && volunteer.levels.length > 0 && (
        <div style={{ marginBottom: 8 }}>
          <strong>层级信息：</strong>
          {volunteer.levels.map((level: any, index: number) => (
            <div key={index} style={{ paddingLeft: 16, fontSize: 12, color: '#666' }}>
              {level.name}: {level.value}
            </div>
          ))}
        </div>
      )}
      <div style={{ marginTop: 12 }}>
        <Space wrap size="small">
          <Button
            type="primary"
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEdit(volunteer)}
          >
            编辑
          </Button>
          <Button
            type="primary"
            danger
            size="small"
            icon={<DeleteOutlined />}
            onClick={() => handleDelete(volunteer.id)}
          >
            删除
          </Button>
        </Space>
      </div>
    </Card>
  );

  const handleEdit = (volunteer: Volunteer) => {
    setEditingVolunteer(volunteer);
    
    // 从doctorVolunteers关联中获取项目ID和负责人信息
    let projectId = volunteer.project_id;
    const volunteerRecord = volunteer as any;
    let responsibleName = volunteer.responsible_person_name;
    let responsiblePhone = volunteer.responsible_person_phone;
    let currentLevels = volunteer.levels; // 默认使用当前对象上的 levels

    if (volunteerRecord.doctorVolunteers && volunteerRecord.doctorVolunteers.length > 0) {
      // 优先取列表筛选的项目ID，否则取第一个
      const targetProjectId = searchParams.project_id || volunteerRecord.doctorVolunteers[0].project_id;
      projectId = targetProjectId;

      // 尝试查找对应项目的绑定记录
      const binding = volunteerRecord.doctorVolunteers.find((dv: any) => dv.project_id === targetProjectId);
      if (binding) {
        if (binding.responsible_person_name) responsibleName = binding.responsible_person_name;
        if (binding.responsible_person_phone) responsiblePhone = binding.responsible_person_phone;
        // 如果绑定记录中有 levels，优先使用（这是项目特有的层级信息）
        if (binding.levels) currentLevels = binding.levels;
      }
    }
    
    // 处理levels字段的格式转换
    let levelsFormData: Array<{order: number, name: string, value: string}> = [];
    
    if (currentLevels && Array.isArray(currentLevels) && currentLevels.length > 0) {
      levelsFormData = (currentLevels as Array<{name: string, value: string, order: number}>)
        .sort((a, b) => (a.order || 0) - (b.order || 0))
        .map((item, index) => ({
          order: item.order || index + 1,
          name: item.name,
          value: item.value
        }));
    }
    
    form.setFieldsValue({
      ...volunteer,
      project_id: projectId,
      levels: levelsFormData,
      responsible_person_name: responsibleName,
      responsible_person_phone: responsiblePhone
    });
    setIsModalVisible(true);
  };

  const handleDelete = (id: number) => {
    Modal.confirm({
      title: '确认删除',
      content: '确定要删除这位志愿者吗？',
      onOk: async () => {
        try {
          const response = await volunteerService.deleteVolunteer(id);
          if (response.code === 0) {
            message.success('删除成功！');
            fetchVolunteers();
          }
        } catch (error) {
          message.error('删除失败');
        }
      },
    });
  };

  const handleModalOk = async () => {
    try {
      const values = await form.validateFields();
      const { levels, ...rest } = values;
      
      // 转换层级数据为新格式
      let levelData: Array<{name: string, value: string, order: number}> = [];
      if (levels && levels.length > 0) {
        levelData = levels
          .filter((level: { name: string; value: string; order: number }) => level.name && level.value)
          .map((level: { name: string; value: string; order: number }) => ({
            name: level.name,
            value: level.value,
            order: level.order || 0
          }))
          .sort((a: {order: number}, b: {order: number}) => a.order - b.order);
      }

      if (editingVolunteer) {
        const response = await volunteerService.updateVolunteer(editingVolunteer.id, {
          ...rest,
          levels: levelData
        });
        if (response.code === 0) {
          message.success('更新成功！');
          setIsModalVisible(false);
          fetchVolunteers();
        }
      }
    } catch (error) {
      console.error('表单验证失败:', error);
    }
  };


  return (
    <div style={{ padding: isMobile ? '16px' : '24px' }}>
      <Row gutter={16} style={{ marginBottom: '24px' }}>
        <Col span={isMobile ? 24 : 6}>
          <Card>
            <Statistic title="总志愿者数" value={pagination.total} />
          </Card>
        </Col>
      </Row>

      <Card style={{ marginBottom: '16px' }}>
        <Form
          form={searchForm}
          layout={isMobile ? 'vertical' : 'inline'}
          onFinish={handleSearch}
          style={{ marginBottom: '16px' }}
        >
          <Form.Item name="project_id" label="所属项目">
            <Select
              style={{ width: isMobile ? '100%' : 200 }}
              allowClear
            >
              {projects.map(project => (
                <Option key={project.id} value={project.id}>{project.name}</Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name="doctor_id" label="关联医生">
            <Select
              style={{ width: isMobile ? '100%' : 300 }}
              allowClear
              showSearch
              loading={doctorSearchLoading}
              placeholder="输入医生姓名搜索"
              filterOption={false}
              onSearch={searchDoctors}
              onClear={() => {
                setDoctors([]);
                setDoctorSearchValue('');
              }}
              notFoundContent={
                doctorSearchLoading
                  ? '搜索中...'
                  : doctorSearchValue.length >= 2
                    ? '未找到匹配的医生'
                    : '请输入至少2个字符开始搜索'
              }
            >
              {doctors.map(doctor => (
                <Option key={doctor.id} value={doctor.id}>
                  {doctor.name} ({doctor.hospitalName} - {doctor.phone})
                </Option>
              ))}
            </Select>
          </Form.Item>
          {levelConfigs.map(level => (
            <Form.Item 
              key={level} 
              name={['levels', level]} 
              label={level}
            >
              <Input style={{ width: isMobile ? '100%' : 200 }} placeholder={`请输入${level}`} />
            </Form.Item>
          ))}
          <Form.Item name="keyword" label="关键字">
            <Input
              placeholder="姓名/手机号"
              prefix={<SearchOutlined />}
              style={{ width: isMobile ? '100%' : 200 }}
            />
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" block={isMobile}>
                搜索
              </Button>
              <Button onClick={handleReset} block={isMobile}>
                重置
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Card>

      {/* 志愿者列表 - PC端表格/移动端卡片 */}
      {!isMobile ? (
        <Table 
          columns={columns} 
          dataSource={volunteers} 
          rowKey="id"
          loading={loading}
          pagination={pagination}
          onChange={(newPagination) => setPagination({
            current: newPagination.current || 1,
            pageSize: newPagination.pageSize || 10,
            total: pagination.total
          })}
          scroll={{ x: 1200 }}
        />
      ) : (
        <>
          <List
            loading={loading}
            dataSource={volunteers}
            renderItem={renderMobileCard}
          />
          {/* 移动端分页 */}
          <div style={{ marginTop: 16, textAlign: 'center' }}>
            <Space direction="vertical" style={{ width: '100%' }}>
              <div style={{ fontSize: 12, color: '#666' }}>
                共 {pagination.total} 条记录，第 {pagination.current}/{Math.ceil(pagination.total / pagination.pageSize)} 页
              </div>
              <Space>
                <Button
                  size="small"
                  disabled={pagination.current === 1}
                  onClick={() => setPagination({ ...pagination, current: pagination.current - 1 })}
                >
                  上一页
                </Button>
                <Button
                  size="small"
                  disabled={pagination.current >= Math.ceil(pagination.total / pagination.pageSize)}
                  onClick={() => setPagination({ ...pagination, current: pagination.current + 1 })}
                >
                  下一页
                </Button>
              </Space>
            </Space>
          </div>
        </>
      )}

      <Modal
        title={editingVolunteer ? '编辑志愿者' : '添加志愿者'}
        open={isModalVisible}
        onOk={handleModalOk}
        onCancel={() => setIsModalVisible(false)}
        width={isMobile ? '100%' : 600}
        style={isMobile ? { top: 0, paddingBottom: 0, maxWidth: '100vw' } : {}}
        bodyStyle={isMobile ? { maxHeight: 'calc(100vh - 110px)', overflowY: 'auto' } : {}}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="name"
            label="姓名"
            rules={[{ required: true, message: '请输入姓名！' }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="phone"
            label="联系电话"
            rules={[
              { required: true, message: '请输入联系电话！' },
              { pattern: /^1[3-9]\d{9}$/, message: '请输入有效的手机号码！' },
            ]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="responsible_person_name"
            label="负责人姓名"
            rules={[{ required: true, message: '请输入负责人姓名！' }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="responsible_person_phone"
            label="负责人电话"
            rules={[
              { required: true, message: '请输入负责人电话！' },
              { pattern: /^1[3-9]\d{9}$/, message: '请输入有效的手机号码！' },
            ]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="project_id"
            label="所属项目"
            rules={[{ required: true, message: '请选择所属项目！' }]}
          >
            <Select onChange={(value) => {
              const selectedProject = projects.find(p => p.id === value);

              // 切换项目时，尝试回填该项目的负责人信息和层级信息
              if (editingVolunteer) {
                const volunteerRecord = editingVolunteer as any;
                if (volunteerRecord.doctorVolunteers) {
                  const binding = volunteerRecord.doctorVolunteers.find((dv: any) => dv.project_id === value);
                  if (binding) {
                    // 1. 准备层级数据
                    let levelFields = [];
                    if (binding.levels && Array.isArray(binding.levels) && binding.levels.length > 0) {
                        // 使用已保存的层级信息
                        levelFields = binding.levels.map((item: any, index: number) => ({
                            order: item.order || index + 1,
                            name: item.name,
                            value: item.value
                        }));
                    } else if (selectedProject?.level_config) {
                        // 使用项目配置生成空层级
                        levelFields = selectedProject.level_config.map((level, index) => ({
                            order: index + 1,
                            name: level,
                            value: ''
                        }));
                    }

                    // 2. 回填表单
                    form.setFieldsValue({
                      responsible_person_name: binding.responsible_person_name || editingVolunteer.responsible_person_name,
                      responsible_person_phone: binding.responsible_person_phone || editingVolunteer.responsible_person_phone,
                      levels: levelFields
                    });
                    return; // 找到了绑定记录并处理完毕，直接返回
                  }
                }
              }
              
              // 如果不是编辑模式，或者没找到绑定记录（理论上编辑模式下筛选过的项目都应该有绑定记录），则使用默认逻辑重置层级
              if (selectedProject?.level_config) {
                const levelFields = selectedProject.level_config.map((level, index) => ({
                  order: index + 1,
                  name: level,
                  value: ''
                }));
                form.setFieldsValue({ levels: levelFields });
              }
            }}>
              {(editingVolunteer ? (
                // 编辑模式：只显示已参与的项目
                projects.filter(p => {
                  const volunteerRecord = editingVolunteer as any;
                  // 如果没有关联记录，显示所有项目（防御性）
                  if (!volunteerRecord.doctorVolunteers || volunteerRecord.doctorVolunteers.length === 0) return true;
                  // 只显示doctorVolunteers中存在的项目
                  return volunteerRecord.doctorVolunteers.some((dv: any) => dv.project_id === p.id);
                })
              ) : (
                // 添加模式：显示所有项目
                projects
              )).map(project => (
                <Option key={project.id} value={project.id}>{project.name}</Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            name="status"
            label="状态"
            rules={[{ required: true, message: '请选择状态！' }]}
          >
            <Select>
              <Option value="active">活跃</Option>
              <Option value="inactive">非活跃</Option>
            </Select>
          </Form.Item>
          <Form.List name="levels">
            {(fields, { add, remove }) => (
              <>
                {fields.map(({ key, name, ...restField }) => (
                  <Space key={key} style={{ display: 'flex', marginBottom: 8 }} align="baseline">
                    <Form.Item
                      {...restField}
                      name={[name, 'order']}
                      rules={[{ required: true, message: '请输入层级序号！' }]}
                    >
                      <Input type="number" placeholder="层级序号" style={{ width: 100 }} disabled />
                    </Form.Item>
                    <Form.Item
                      {...restField}
                      name={[name, 'name']}
                      rules={[{ required: true, message: '请输入层级名称！' }]}
                    >
                      <Input placeholder="层级名称" style={{ width: 150 }} disabled />
                    </Form.Item>
                    <Form.Item
                      {...restField}
                      name={[name, 'value']}
                      rules={[{ required: true, message: '请输入层级值！' }]}
                    >
                      <Input placeholder="层级值" />
                    </Form.Item>
                    <Button type="link" onClick={() => remove(name)} disabled>
                      删除
                    </Button>
                  </Space>
                ))}
                <Button type="dashed" onClick={() => add()} block disabled>
                  添加层级
                </Button>
              </>
            )}
          </Form.List>
        </Form>
      </Modal>
    </div>
  );
};

export default VolunteerManagement; 